/**
 * intradayCurve.js
 *
 * Builds the equity curve of an intraday futures strategy (EOS, ARTEMIS, HELIOS) in the browser
 * from its per-session return at ONE contract, so the visitor can choose the exposure:
 *
 *   exposicion   0.5 | 1 | 2 | 3 | 5   → notional held while in position, as a multiple of the
 *                                         account (1 = 100 % of the account, 3 = 300 %)
 *
 * The contracts follow from that: contracts = exposicion × account / (price × $2). Constant
 * exposure over the whole history, with fractional contracts (see `contratosHoy` for the whole
 * number an account would actually trade today).
 *
 * `pnl` is the JSON written by scripts/intraday.py:
 *   { desde, hasta, exposicion_default, exposiciones, sesiones: [[date, pnl1c, ret1c], ...] }
 * with ret1c the session's return of one contract over its notional at entry (gross of costs).
 * The output has the same shape as the strategy JSONs: [{ date, value }] rebased to 100.
 */

export const SIZING_DEFAULT = { exposicion: 3 };
export const EXPOSICION_OPCIONES = [0.5, 1, 2, 3, 5];
export const MNQ_MULTIPLICADOR = 2;

function previousBusinessDay(iso) {
  const d = new Date(iso + 'T00:00:00Z');
  do { d.setUTCDate(d.getUTCDate() - 1); } while (d.getUTCDay() === 0 || d.getUTCDay() === 6);
  return d.toISOString().slice(0, 10);
}

export function buildIntradayCurve(pnl, sizing = SIZING_DEFAULT) {
  if (!pnl?.sesiones?.length) return [];
  const exposicion = Number(sizing?.exposicion) || SIZING_DEFAULT.exposicion;

  const byDate = new Map(pnl.sesiones.map((s) => [s[0], s[2]]));
  const first = pnl.sesiones[0][0];
  const lastTrade = pnl.sesiones[pnl.sesiones.length - 1][0];
  const last = pnl.hasta && pnl.hasta > lastTrade ? pnl.hasta : lastTrade;

  const curve = [{ date: previousBusinessDay(first), value: 100 }];
  let nav = 100;
  const d = new Date(first + 'T00:00:00Z');
  const end = new Date(last + 'T00:00:00Z');
  while (d <= end) {
    const dow = d.getUTCDay();
    if (dow !== 0 && dow !== 6) {
      const iso = d.toISOString().slice(0, 10);
      nav *= 1 + exposicion * (byDate.get(iso) || 0);
      if (nav < 0) nav = 0;
      curve.push({ date: iso, value: Math.round(nav * 10000) / 10000 });
    }
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return curve;
}

/** Whole contracts an account of `cuenta` dollars would hold today at this exposure. */
export function contratosHoy(exposicion, cuenta, precio) {
  if (!precio || !cuenta) return null;
  return Math.floor((exposicion * cuenta) / (precio * MNQ_MULTIPLICADOR));
}

/** Smallest account that can hold one whole contract at this exposure. */
export function cuentaMinima(exposicion, precio) {
  if (!precio || !exposicion) return null;
  return (precio * MNQ_MULTIPLICADOR) / exposicion;
}
